// src/components/admin/Views/SeriesList.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../../assets/styles/series-list.scss';
import {
    fetchSeries,
    deleteSeries,
    updateSeries,
    type Series,
} from '../../../../api/series';

type ColumnKey =
    | 'language'
    | 'is_visible'
    | 'is_premium'
    | 'created_at'
    | 'description'
    | 'actions';

const pageSize = 20;

// domyślna widoczność kolumn
const DEFAULT_VISIBLE_COLS: Record<ColumnKey, boolean> = {
    language: true,
    is_visible: true,
    is_premium: true,
    created_at: true,
    description: true,
    actions: true,
};

// etykiety do panelu widoczności
const COLUMN_LABELS: Record<ColumnKey, string> = {
    language: 'Język',
    is_visible: 'Widoczność',
    is_premium: 'Premium',
    created_at: 'Data',
    description: 'Opis',
    actions: 'Akcje',
};

export default function SeriesList() {
    const navigate = useNavigate();

    // filters
    const [search, setSearch] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [visibleFilter, setVisibleFilter] = useState<'any' | 'true' | 'false'>('any');
    const [premiumFilter, setPremiumFilter] = useState<'any' | 'true' | 'false'>('any');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [showDateFilters, setShowDateFilters] = useState(false);

    // data
    const [data, setData] = useState<{ items: Series[]; total: number; page: number; limit: number }>({
        items: [],
        total: 0,
        page: 1,
        limit: pageSize,
    });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // visible columns (z localStorage, jak w Users.tsx)
    const [visibleCols, setVisibleCols] = useState<Record<ColumnKey, boolean>>(() => {
        try {
            const saved = localStorage.getItem('series_visibleCols');
            return saved ? { ...DEFAULT_VISIBLE_COLS, ...JSON.parse(saved) } : DEFAULT_VISIBLE_COLS;
        } catch {
            return DEFAULT_VISIBLE_COLS;
        }
    });
    useEffect(() => {
        localStorage.setItem('series_visibleCols', JSON.stringify(visibleCols));
    }, [visibleCols]);

    // panel widoczności
    const [colsOpen, setColsOpen] = useState(false);
    const colsBtnRef = useRef<HTMLButtonElement | null>(null);
    const colsPanelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!colsOpen) return;

        function onDocMouseDown(e: MouseEvent) {
            const t = e.target as Node;
            if (colsPanelRef.current?.contains(t) || colsBtnRef.current?.contains(t)) return;
            setColsOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setColsOpen(false);
        }

        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [colsOpen]);

    // confirm delete
    const [confirmId, setConfirmId] = useState<number | null>(null);
    const [confirmTitle, setConfirmTitle] = useState<string>('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // fetch list
    async function load() {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchSeries({
                query: search.trim(),
                page,
                limit: pageSize,
                language: languageFilter || undefined,
                is_visible: visibleFilter,
                is_premium: premiumFilter,
            });
            setData(res);
        } catch (e: any) {
            setError(e?.message || 'Błąd pobierania serii');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, languageFilter, visibleFilter, premiumFilter, search]);

    // local date filter (klientowo na tym co przyszło z API)
    const filtered = useMemo(() => {
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        return data.items.filter(s => {
            const created = new Date(s.created_at);
            return (!from || created >= from) && (!to || created <= to);
        });
    }, [data.items, dateFrom, dateTo]);

    const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

    // actions
    const openConfirm = (s: Series) => {
        setConfirmId(s.id);
        setConfirmTitle(s.title);
        setDeleteError(null);
    };
    const closeConfirm = () => {
        setConfirmId(null);
        setConfirmTitle('');
        setDeleteError(null);
        setDeleting(false);
    };
    const doDelete = async () => {
        if (!confirmId) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteSeries(confirmId);
            closeConfirm();
            if (filtered.length === 1 && page > 1) setPage(p => p - 1);
            else load();
        } catch (e: any) {
            const msg = e?.error || e?.message || 'Nie udało się usunąć';
            const low = String(msg).toLowerCase();
            if (low.includes('owner') || low.includes('only owner') || low.includes('admins only') || low.includes('not allowed') || low.includes('forbidden')) {
                setDeleteError('Nie masz uprawnień do usunięcia tej serii.');
            } else {
                setDeleteError(msg);
            }
            setDeleting(false);
        }
    };

    const toggleVisible = async (s: Series) => {
        try {
            const upd = await updateSeries(s.id, { is_visible: !s.is_visible });
            setData(prev => ({ ...prev, items: prev.items.map(it => it.id === s.id ? upd : it) }));
        } catch (e: any) {
            alert(e?.message || 'Nie udało się zmienić widoczności');
        }
    };

    const togglePremium = async (s: Series) => {
        try {
            const upd = await updateSeries(s.id, { is_premium: !s.is_premium });
            setData(prev => ({ ...prev, items: prev.items.map(it => it.id === s.id ? upd : it) }));
        } catch (e: any) {
            alert(e?.message || 'Nie udało się zmienić statusu premium');
        }
    };

    const visibleCount = useMemo(() => Object.values(visibleCols).filter(Boolean).length, [visibleCols]);

    return (
        <div className="contents-list-container">
            <div className="contents-header-bar">
                <h2>Serie</h2>
                <div className="action-buttons">
                    <button className="btn ghost" onClick={() => setFiltersOpen(v => !v)}>
                        <span className="btn-icon" aria-hidden>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="7"></circle>
                                <path d="M21 21l-4.3-4.3"></path>
                            </svg>
                        </span>
                        Filtry
                    </button>
                    <button className="btn primary" onClick={() => navigate('/admin/series/new')}>
                        <span className="ico" aria-hidden>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </span>
                        <span className="label">Dodaj serie</span>
                    </button>
                </div>
            </div>

            {filtersOpen && (
                <div className="filters-container">
                    <div className="filters-row">
                        <input
                            type="text"
                            placeholder="Szukaj po tytule…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />

                        <button className="date-toggle-btn" onClick={() => setShowDateFilters(v => !v)}>Data</button>
                        {showDateFilters && (
                            <>
                                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                            </>
                        )}

                        <select value={languageFilter} onChange={e => { setLanguageFilter(e.target.value); setPage(1); }}>
                            <option value="">Język</option>
                            {[...new Set(data.items.map(i => i.language).filter(Boolean) as string[])].map(l => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </select>

                        <select className="filter-narrow" value={visibleFilter} onChange={e => { setVisibleFilter(e.target.value as any); setPage(1); }}>
                            <option value="any">Widoczność</option>
                            <option value="true">Tylko widoczne</option>
                            <option value="false">Ukryte</option>
                        </select>

                        <select className="filter-narrow" value={premiumFilter} onChange={e => { setPremiumFilter(e.target.value as any); setPage(1); }}>
                            <option value="any">Premium</option>
                            <option value="true">Tylko premium</option>
                            <option value="false">Nie premium</option>
                        </select>

                        <button
                            className="btn btn-primary"
                            disabled={loading}
                            onClick={() => { setPage(1); load(); }}
                        >
                            {loading ? 'Ładowanie…' : 'Szukaj'}
                        </button>
                    </div>
                </div>
            )}

            <div className="cols-control series">
                <button
                    type="button"
                    className="btn ghost cols-btn"
                    onClick={() => setColsOpen(v => !v)}
                    aria-expanded={colsOpen}
                    title="Widoczność kolumn"
                    ref={colsBtnRef}
                >
                    <span className="btn-icon" aria-hidden>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                    </span>
                    Widoczność
                </button>

                {colsOpen && (
                    <div
                        className="cols-panel"
                        role="dialog"
                        aria-label="Wybór kolumn"
                        ref={colsPanelRef}
                    >
                        {(Object.keys(COLUMN_LABELS) as ColumnKey[]).map(k => (
                            <label key={k} className="filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={!!visibleCols[k]}
                                    onChange={(e) => setVisibleCols(v => ({ ...v, [k]: e.target.checked }))}
                                />
                                {COLUMN_LABELS[k]}
                            </label>
                        ))}
                    </div>
                )}
            </div>

            <table className="contents-table">
                <thead>
                    <tr>
                        <th className="col-id">ID</th>
                        <th className="col-title">Tytuł</th>
                        {visibleCols.language && <th className="col-lang">Język</th>}
                        {visibleCols.is_visible && <th>Widoczność</th>}
                        {visibleCols.is_premium && <th>Premium</th>}
                        {visibleCols.created_at && <th>Data</th>}
                        {visibleCols.description && <th>Opis</th>}
                        {visibleCols.actions && <th></th>}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan={2 + visibleCount}>Ładowanie…</td></tr>
                    ) : (
                        filtered.map(s => (
                            <tr key={s.id}>
                                <td className="col-id-val">{s.id}</td>
                                <td className="ellipsis" title={s.title}>{s.title}</td>
                                {visibleCols.language && (
                                    <td className="ellipsis" title={s.language || '—'}>{s.language || '—'}</td>
                                )}
                                {visibleCols.is_visible && (
                                    <td className="strong">
                                        <span className={`badge ${s.is_visible ? 'badge-success' : 'badge-muted'}`}>
                                            {s.is_visible ? 'TAK' : 'NIE'}
                                        </span>
                                    </td>
                                )}
                                {visibleCols.is_premium && (
                                    <td className="strong">
                                        <span className={`badge ${s.is_premium ? 'badge-success' : 'badge-muted'}`}>
                                            {s.is_premium ? 'TAK' : 'NIE'}
                                        </span>
                                    </td>
                                )}
                                {visibleCols.created_at && (
                                    <td>{new Date(s.created_at).toLocaleDateString()}</td>
                                )}
                                {visibleCols.description && (
                                    <td className="strong">
                                        <span className={`badge ${s.description ? 'badge-success' : 'badge-muted'}`}>
                                            {s.description ? 'TAK' : 'NIE'}
                                        </span>
                                    </td>
                                )}

                                {visibleCols.actions && (
                                    <td className="actions-cell">
                                        <button className="icon-btn edit" onClick={() => navigate(`/admin/series/edit/${s.id}`)} aria-label="Edytuj">
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 20h9"></path>
                                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                                            </svg>
                                        </button>
                                        <button className="icon-btn delete" onClick={() => openConfirm(s)} aria-label="Usuń">
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18"></path>
                                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                            </svg>
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                    {!loading && filtered.length === 0 && (
                        <tr>
                            <td colSpan={2 + visibleCount} className="empty">Brak serii</td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className="pagination">
                <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>◀</button>
                <span>Strona {page} / {Math.max(1, Math.ceil(data.total / pageSize))}</span>
                <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>▶</button>
            </div>

            {error && <div className="alert error">{error}</div>}

            {confirmId !== null && (
                <div className="modal-overlay" onClick={closeConfirm}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>Czy na pewno usunąć serię?</h3>
                        <p className="modal-desc">„{confirmTitle}” zostanie trwale usunięta.</p>
                        {deleteError && <div className="modal-error">{deleteError}</div>}
                        <div className="modal-actions">
                            <button onClick={doDelete} disabled={deleting}>{deleting ? 'Usuwanie…' : 'Tak, usuń'}</button>
                            <button className="secondary" onClick={closeConfirm}>Anuluj</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
